import { useState, useMemo } from "react";
import { C, STEP_BASED } from "../constants";
import { toDisplayWeight, toDisplayDist, weightLabel, distLabel } from "../utils/units";
import { fmtDate, fmtDuration } from "../utils/dateHelpers";
import { Badge } from "./ui";
import { WorkoutRow } from "./WorkoutRow";

export const HistoryTab = ({ workouts, filterType, setFilterType, settings }) => {
  const [expandedSessions, setExpandedSessions] = useState({});
  const toggleSession = id => setExpandedSessions(p => ({ ...p, [id]: !p[id] }));
  const wl = weightLabel(settings.weightUnit);

  const items = useMemo(() => {
    const filtered = workouts.filter(w => filterType === "all" || w.type === filterType);
    const sorted   = [...filtered].sort((a, b) => b.date.localeCompare(a.date) || (b.id > a.id ? 1 : -1));
    const seen     = new Set();
    const result   = [];

    sorted.forEach(w => {
      if (w.sessionId) {
        if (!seen.has(w.sessionId)) {
          seen.add(w.sessionId);
          const sessionEntries = sorted.filter(x => x.sessionId === w.sessionId);
          const byExercise = {};
          sessionEntries.forEach(e => {
            if (e.type === "cardio") {
              byExercise[`_c_${e.id}`] = {
                name: e.name || e.cardioType, type: "cardio",
                cardioType: e.cardioType,
                duration: e.duration, distance: e.distance, calories: e.calories,
                sets: [],
                completionOrder: e.completionOrder ?? null,
              };
            } else {
              if (!byExercise[e.name]) byExercise[e.name] = {
                name: e.name, muscleGroup: e.muscleGroup, type: "strength", sets: [],
                completionOrder: e.completionOrder ?? null,
              };
              byExercise[e.name].sets.push({ reps: e.reps, weight: e.weight, isDropset: !!e.isDropset, isMyoRep: !!e.isMyoRep });
              // Keep the earliest completionOrder seen across this exercise's sets
              if (e.completionOrder != null) {
                const cur = byExercise[e.name].completionOrder;
                if (cur == null || e.completionOrder < cur) byExercise[e.name].completionOrder = e.completionOrder;
              }
            }
          });
          const exercises = Object.values(byExercise);
          // Sort by actual execution order — entries without completionOrder fall to the end but keep their relative insertion order (stable sort)
          exercises.sort((a, b) => {
            const ao = a.completionOrder ?? Infinity;
            const bo = b.completionOrder ?? Infinity;
            return ao - bo;
          });
          const sessionTitle = w.planName || (
            exercises.length === 1
              ? exercises[0].name
              : exercises.slice(0, 2).map(e => e.name).join(" · ") + (exercises.length > 2 ? " ···" : "")
          );
          result.push({
            kind: "session", sessionId: w.sessionId, date: w.date,
            planName: sessionTitle, elapsedSecs: w.elapsedSecs,
            exercises,
          });
        }
      } else {
        result.push({ kind: "single", workout: w });
      }
    });

    return result;
  }, [workouts, filterType]);

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["all", "strength", "cardio"].map(f => (
          <button key={f} onClick={() => setFilterType(f)} style={{
            padding: "7px 16px", borderRadius: 8,
            border: `1px solid ${filterType === f ? C.accent : C.border}`,
            background: filterType === f ? C.accent : "transparent",
            color: filterType === f ? "#1e1d1b" : C.muted,
            fontWeight: 600, fontSize: 12, cursor: "pointer",
            textTransform: "capitalize", letterSpacing: "0.01em",
          }}>{f}</button>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map(item => {
          if (item.kind === "single") {
            return <WorkoutRow key={item.workout.id} w={item.workout} settings={settings} />;
          }

          const isOpen        = !!expandedSessions[item.sessionId];
          const strengthExs   = item.exercises.filter(e => e.type === "strength");
          const muscleGroups  = [...new Set(strengthExs.map(e => e.muscleGroup))];
          const totalSets     = strengthExs.reduce((s, e) => s + e.sets.length, 0);
          const statsLine     = [
            `${item.exercises.length} exercise${item.exercises.length !== 1 ? "s" : ""}`,
            totalSets > 0 && `${totalSets} set${totalSets !== 1 ? "s" : ""}`,
          ].filter(Boolean).join(" · ");

          return (
            <div key={item.sessionId} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div onClick={() => toggleSession(item.sessionId)} style={{ padding: "14px 16px", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, flexShrink: 0, background: "#5c5e60", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="1"    y="8"   width="3"   height="4"  rx="1" fill="#f0eeeb"/>
                      <rect x="3"    y="6"   width="2.5" height="8"  rx="1" fill="#f0eeeb"/>
                      <rect x="14.5" y="6"   width="2.5" height="8"  rx="1" fill="#f0eeeb"/>
                      <rect x="16"   y="8"   width="3"   height="4"  rx="1" fill="#f0eeeb"/>
                      <rect x="5.5"  y="9"   width="9"   height="2"  rx="1" fill="#f0eeeb"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{item.planName}</span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {muscleGroups.map(g => <Badge key={g}>{g}</Badge>)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ color: C.muted, fontSize: 11, marginBottom: 3 }}>{fmtDate(item.date)}</div>
                    <div style={{ color: C.textDim, fontSize: 11 }}>{statsLine}</div>
                    {item.elapsedSecs && <div style={{ color: C.muted, fontSize: 11 }}>{fmtDuration(item.elapsedSecs)}</div>}
                  </div>
                  <div style={{ color: C.muted, fontSize: 14, marginLeft: 8 }}>{isOpen ? "▲" : "▼"}</div>
                </div>
              </div>

              {isOpen && (
                <div style={{ borderTop: `1px solid ${C.border}` }}>
                  {item.exercises.map((ex, ei) => {
                    const isLast = ei === item.exercises.length - 1;
                    const rowStyle = {
                      padding: "11px 16px",
                      borderBottom: isLast ? "none" : `1px solid ${C.border}`,
                      display: "flex", alignItems: "flex-start", gap: 12,
                    };

                    if (ex.type === "cardio") {
                      const isStep = STEP_BASED.includes(ex.cardioType);
                      const dispDist = isStep
                        ? ex.distance.toLocaleString()
                        : `${toDisplayDist(ex.distance, settings.distUnit).toFixed(2)} ${settings.distUnit}`;
                      return (
                        <div key={`_c_${ei}`} style={rowStyle}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                              <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{ex.name}</span>
                              <Badge color="blue">{ex.cardioType}</Badge>
                            </div>
                            <div style={{ color: C.muted, fontSize: 12 }}>
                              {ex.duration > 0 && `${ex.duration} min`}
                              {ex.distance > 0 && ` · ${isStep ? ex.distance.toLocaleString() + " steps" : dispDist}`}
                              {ex.calories > 0 && ` · ${ex.calories} kcal`}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (!ex.sets.length) return null;
                    const workingSets = ex.sets.filter(s => !s.isDropset && !s.isMyoRep);
                    const bestPool    = workingSets.length ? workingSets : ex.sets;
                    const bestSet     = bestPool.reduce((best, s) => s.weight > best.weight ? s : best, bestPool[0]);
                    const dispW       = toDisplayWeight(bestSet.weight, settings.weightUnit);
                    return (
                      <div key={ex.name} style={rowStyle}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{ex.name}</span>
                            <Badge>{ex.muscleGroup}</Badge>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                            {ex.sets.map((s, si) => {
                              const dw = toDisplayWeight(s.weight, settings.weightUnit);
                              const chipStyle = s.isDropset
                                ? { background: C.blue   + "15", color: C.blue,    border: `1px solid ${C.blue}44`   }
                                : s.isMyoRep
                                ? { background: C.orange + "15", color: C.orange,  border: `1px solid ${C.orange}44` }
                                : { background: C.surface,       color: C.textDim, border: `1px solid ${C.border}`   };
                              return (
                                <div key={si} style={{ padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, ...chipStyle }}>
                                  {s.isDropset && <span style={{ fontSize: 9, fontWeight: 700, marginRight: 4, letterSpacing: "0.06em" }}>DS</span>}
                                  {s.isMyoRep  && <span style={{ fontSize: 9, fontWeight: 700, marginRight: 4, letterSpacing: "0.06em" }}>+</span>}
                                  {s.weight > 0 ? `${dw}${wl} × ${s.reps}` : `BW × ${s.reps}`}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Best</div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
                            {bestSet.weight > 0 ? `${dispW} ${wl}` : "BW"}
                          </div>
                          <div style={{ fontSize: 11, color: C.muted }}>× {bestSet.reps}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {items.length === 0 && (
          <div style={{ color: C.muted, textAlign: "center", padding: "40px 0" }}>No workouts found</div>
        )}
      </div>
    </>
  );
};
